import React from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import {
  AccountBalance as BankIcon, RequestQuote as OwedIcon, ReceiptLong as OweIcon, TrendingUp as ProfitIcon,
} from '@mui/icons-material';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as ChartTooltip, CartesianGrid } from 'recharts';
import HeadlineCard from '../../components/dashboard/v2/HeadlineCard';
import DashCard from '../../components/dashboard/v2/DashCard';
import HealthRing from '../../components/dashboard/v2/HealthRing';
import PersonRow, { initialsOf, colorOf } from '../../components/dashboard/v2/PersonRow';
import { toneColor, toneSoft } from '../../components/dashboard/v2/styles';
import { formatAed, formatCompact } from '../../utils/dashboard/metrics';
import { IconTile, gridSx } from './OperationsTab';

/** Finance tab — "is my money OK?" (TC-DASHBOARD-V2-DESIGN §4.2, mockup Finance). */

const fmt = (v) => Math.round(Number(v) || 0).toLocaleString('en-US');

// Health item action code → page (same targets as the Financial Health Check page)
const HEALTH_ROUTES = {
  REVIEW_ACCOUNT: (id) => (id ? `/reports/wallet-bank?account=${id}` : '/reports/wallet-bank'),
  APPROVE_TOPUP: () => '/wallets?tab=topups',
  PAY_EXPENSE: (id) => (id ? `/accounting/expenses/${id}` : '/accounting/expenses'),
  PAY_COMMISSIONS: () => '/accounting/commissions',
  FILE_VAT: () => '/reports/vat-return',
  PAY_VAT: () => '/reports/vat-return',
  REVIEW_CHEQUE: () => '/banking?tab=cheques',
  RUN_DEPRECIATION: () => '/accounting/assets',
};

// same labels as the Cash Position report
const ACCOUNT_TYPES = { BANK: 'Bank', CASH: 'Cash drawer', WALLET: 'E-wallet', E_WALLET: 'E-wallet', CREDIT_CARD: 'Credit card' };

const AGING = [
  { key: 'current', label: 'Current', color: '#22c55e' },
  { key: 'd1to30', label: '1–30d', color: '#f59e0b' },
  { key: 'd31to60', label: '31–60d', color: '#f97316' },
  { key: 'd60plus', label: '60d+', color: '#dc2626' },
];

const Pill = ({ tone, children }) => {
  const theme = useTheme();
  return (
    <Box component="span" sx={{ fontSize: '0.6875rem', fontWeight: 700, px: 1, py: 0.25, borderRadius: 999, whiteSpace: 'nowrap', bgcolor: toneSoft(theme, tone), color: toneColor(theme, tone) }}>
      {children}
    </Box>
  );
};

const SoftButton = ({ onClick, children, strong }) => (
  <Box component="button" type="button" onClick={onClick}
    sx={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: '0.75rem', fontWeight: 700, px: 1.75, py: 0.75, borderRadius: '9px', whiteSpace: 'nowrap',
      bgcolor: strong ? 'primary.main' : 'action.selected', color: strong ? 'primary.contrastText' : 'primary.main' }}>
    {children}
  </Box>
);

const FinanceTab = ({ model, navigate, periodLabel, user }) => {
  const theme = useTheme();
  const m = model;
  const e = m.empty ? {} : (m.errors || {});
  const none = m.empty;
  const ownShare = m.cash.total > 0 ? Math.max(0, Math.min(100, (m.cash.ownMoney / m.cash.total) * 100)) : 100;
  const agingTotal = AGING.reduce((s, b) => s + (m.aging[b.key] || 0), 0);
  const moreWallets = m.wallets.top.slice(2);
  const moreTotal = moreWallets.reduce((s, w) => s + w.balance, 0);

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={gridSx('repeat(4, minmax(0, 1fr))')}>
        <HeadlineCard hero label="Cash & bank" icon={<IconTile Icon={BankIcon} hero />} value={none || e.cash ? '—' : formatAed(m.cash.total)}
          fact={none ? 'No data yet' : e.cash} onClick={() => navigate('/reports/cash-position')}>
          {!none && !e.cash && (
            <Box sx={{ mt: 1.25 }}>
              <Box sx={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', bgcolor: 'rgba(255,255,255,.18)' }}>
                <Box sx={{ width: `${ownShare}%`, bgcolor: '#fff' }} />
                <Box sx={{ flex: 1, bgcolor: '#fbbf24' }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.75, fontSize: '0.71875rem' }}>
                <span><b>{fmt(m.cash.ownMoney)}</b> <Box component="span" sx={{ opacity: 0.8 }}>your money</Box></span>
                <span><b>{fmt(m.cash.customerWallets)}</b> <Box component="span" sx={{ opacity: 0.8 }}>customer wallets</Box></span>
              </Box>
            </Box>
          )}
        </HeadlineCard>
        <HeadlineCard label="Customers owe you" icon={<IconTile Icon={OwedIcon} tone="amber" />} value={none || e.aging ? '—' : formatAed(m.owed.total)}
          fact={none ? 'No data yet' : e.aging}
          delta={!none && !e.aging && m.owed.overduePct > 0 ? { direction: 'down' } : undefined}
          deltaText={!none && !e.aging && m.owed.overduePct > 0 ? `${m.owed.overduePct}% overdue` : undefined}
          onClick={() => navigate('/reports/receivables-aging')} />
        <HeadlineCard label="You owe" icon={<IconTile Icon={OweIcon} tone="red" />}
          value={none || e.health || m.youOwe.total == null ? '—' : formatAed(m.youOwe.total)}
          fact={none || (!e.health && m.youOwe.total == null) ? 'No data yet' : e.health || 'Thaklis · bills · cards · VAT'} />
        <HeadlineCard label={`Net profit · ${periodLabel}`} icon={<IconTile Icon={ProfitIcon} tone="green" />}
          value={none || e.netProfit ? '—' : formatAed(m.netProfit.amount)} tone={!none && m.netProfit.amount < 0 ? 'red' : undefined}
          delta={none ? undefined : m.netProfit.delta} fact={none ? 'No data yet' : e.netProfit}
          onClick={() => navigate('/reports/profit-loss')} />
      </Box>

      <Box sx={gridSx('8fr 4fr')}>
        <DashCard title="Cash in vs out" empty={none} error={e.cashFlow} linkLabel="Daily sheet →" onLink={() => navigate('/reports/daily-sheet')}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: -1, mb: 1 }}>Last 30 days</Typography>
          <Box sx={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={m.cashFlow.days.map((d) => ({ ...d, net: d.in - d.out, day: d.date.slice(8, 10) }))} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={theme.palette.divider} />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} interval={4} />
                <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} tickFormatter={formatCompact} />
                <ChartTooltip formatter={(v) => fmt(v)} />
                <Bar animationBegin={250} animationDuration={700} animationEasing="ease-out" dataKey="in" name="In" fill="#22c55e" radius={[3, 3, 0, 0]} />
                <Bar animationBegin={250} animationDuration={700} animationEasing="ease-out" dataKey="out" name="Out" fill="#fca5a5" radius={[3, 3, 0, 0]} />
                <Line animationBegin={450} animationDuration={800} animationEasing="ease-out" dataKey="net" name="Net" stroke={theme.palette.text.primary} strokeWidth={1.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </DashCard>
        <DashCard title="Financial health" empty={none || m.health.empty} error={e.health} linkLabel="Details →" onLink={() => navigate('/accounting/health')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <HealthRing passed={m.health.passed} total={m.health.total} />
          </Box>
          {m.health.items.map((it, i) => (
            <Box key={`${it.title}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderTop: '1px solid', borderColor: 'divider', mt: i ? 0 : 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap title={it.title}>{it.title}</Typography>
                {it.detail != null && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{formatAed(it.detail)}</Typography>}
              </Box>
              <SoftButton strong={i === 0} onClick={() => navigate((HEALTH_ROUTES[it.action.code] || (() => '/accounting/health'))(it.action.entityId))}>
                {it.action.label}
              </SoftButton>
            </Box>
          ))}
        </DashCard>
      </Box>

      <Box sx={gridSx('5fr 3fr 4fr')}>
        <DashCard title="Who owes you" empty={none} error={e.aging} linkLabel="Aging report →" onLink={() => navigate('/reports/receivables-aging')}>
          {agingTotal > 0 && (
            <Box sx={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', gap: '3px', mb: 1 }}>
              {AGING.filter((b) => m.aging[b.key] > 0).map((b) => <Box key={b.key} sx={{ flex: m.aging[b.key], bgcolor: b.color }} />)}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
            {AGING.map((b) => (
              <Box key={b.key} component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: b.color }} />{`${b.label} ${formatCompact(m.aging[b.key])}`}
              </Box>
            ))}
          </Box>
          {m.aging.top.map((r, i) => (
            <PersonRow key={r.customerId} index={i} initials={initialsOf(r.name)} color={colorOf(r.name)} title={r.name} sub={r.oldest} right={fmt(r.amount)}
              onClick={() => navigate(`/reports/client-ledger/${r.customerId}`)} />
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: toneSoft(theme, 'amber') }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip title="Government fees you paid for customers, not yet collected from them">
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'help' }}>
                  Govt fees not yet collected ⓘ
                </Typography>
              </Tooltip>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{m.govtFloat.failed ? 'Could not load' : `Paid ${fmt(m.govtFloat.paid)} · collected ${fmt(m.govtFloat.collected)}${m.govtFloat.writtenOff > 0 ? ` · written off ${fmt(m.govtFloat.writtenOff)}` : ''}`}</Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, color: toneColor(theme, 'amber') }}>{m.govtFloat.failed ? '—' : fmt(m.govtFloat.float)}</Typography>
          </Box>
        </DashCard>

        <DashCard title="Banks & cash" empty={none} error={e.cash} linkLabel="Wallets →" onLink={() => navigate('/wallets')}>
          {m.accounts.map((a, i) => (
            <PersonRow key={a.id} index={i} initials={initialsOf(a.name)} color={colorOf(a.name)} title={a.name} sub={ACCOUNT_TYPES[a.type] || 'Bank'} right={fmt(a.balance)} />
          ))}
          {user?.thaklisEnabled !== false && <PersonRow initials="TK" color="#7c3aed"
            title={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>Thaklis wallet</Box>}
            sub={m.thaklisWallet.failed ? <Pill tone="red">Could not load</Pill> : m.thaklisWallet.pendingTopups > 0 ? <Pill tone="amber">{`${m.thaklisWallet.pendingTopups} top-up${m.thaklisWallet.pendingTopups === 1 ? '' : 's'} pending`}</Pill> : null}
            right={m.thaklisWallet.failed ? '—' : fmt(m.thaklisWallet.balance)} />}
        </DashCard>

        <DashCard title={(
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            Customer wallets
            <Tooltip title="Customers paid this in advance and it is not settled yet — it is their money, not income">
              <Box component="span" sx={{ cursor: 'help' }}><Pill tone="amber">liability ⓘ</Pill></Box>
            </Tooltip>
          </Box>
        )} empty={none} error={e.wallets} linkLabel="Wallets →" onLink={() => navigate('/reports/customer-advances-debtors')}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-.02em' }}>{formatAed(m.wallets.total)}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{`${m.wallets.customers} customer${m.wallets.customers === 1 ? '' : 's'}`}</Typography>
          </Box>
          {m.wallets.top.slice(0, 2).map((w, i) => (
            <PersonRow key={w.customerId} index={i} initials={initialsOf(w.name)} color={colorOf(w.name)} title={w.name} right={fmt(w.balance)} />
          ))}
          {moreWallets.length > 0 && (
            <PersonRow initials={`+${moreWallets.length}`} color="#64748b" title={`${moreWallets.length} more customer${moreWallets.length === 1 ? '' : 's'}`} right={fmt(moreTotal)} />
          )}
          {m.wallets.idle.count > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pt: 1.25, mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {`${m.wallets.idle.count} wallet${m.wallets.idle.count === 1 ? '' : 's'} unused for 90+ days`}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{formatAed(m.wallets.idle.amount)}</Typography>
              </Box>
              <SoftButton onClick={() => navigate('/reports/customer-advances-debtors')}>Review</SoftButton>
            </Box>
          )}
        </DashCard>
      </Box>
    </Box>
  );
};

export default FinanceTab;
